import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Search, RotateCcw, Car } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { PreventivoSection } from "@/components/PreventivoSection";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { Faq } from "@/components/Faq";
import { UsatoCard, cvDi } from "@/components/UsatoCard";
import { useSeo, faqSchema, SITE_URL } from "@/lib/seo";
import { CATEGORIE_USATO, FAQ_USATO, slugify } from "@/data/usato";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALIMENTAZIONI = ["Benzina", "Diesel", "Hybrid", "Plug-in Hybrid", "Elettrico"];
const POTENZE = [
  { value: "0-100", label: "Fino a 100 CV", min: 0, max: 100 },
  { value: "101-150", label: "101 – 150 CV", min: 101, max: 150 },
  { value: "151-200", label: "151 – 200 CV", min: 151, max: 200 },
  { value: "201-", label: "Oltre 200 CV", min: 201, max: 9999 },
];
const TIPI = [
  { value: "auto", label: "Auto" },
  { value: "commerciale", label: "Veicoli commerciali" },
  { value: "moto", label: "Moto e scooter" },
];
const selectCls = "w-full rounded-xl border border-xmove-ink/10 bg-white px-3 py-2.5 text-sm text-xmove-ink outline-none focus:border-xmove-orange";

const itemListSchema = (lista, url) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  url,
  numberOfItems: lista.length,
  itemListElement: lista.slice(0, 50).map((a, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `${SITE_URL}/usato/${a.slug}/`,
    name: `${a.marca} ${a.modello} ${a.anno || ""}`.trim(),
  })),
});

/** Testo SEO della pagina: solo fatti indicati dal titolare. */
const TestoUsato = ({ landing }) => (
  <section className="mx-auto max-w-3xl px-5 py-16 lg:px-8" data-testid="usato-seo-content">
    <Reveal>
      <div className="space-y-5 text-base leading-relaxed text-xmove-muted">
        <h2 className="font-display text-2xl font-bold text-xmove-ink">{landing ? `${landing.nome}: come funziona` : "Noleggio lungo termine usato: perché conviene"}</h2>
        <p>
          Il noleggio lungo termine usato di XMOVE (SecondLife, o NLT Re-Use) funziona come il noleggio a lungo termine che già conosci, ma su
          auto che hanno appena concluso un primo ciclo di noleggio e hanno al massimo 70/80.000 km.
          Non tutte rientrano nel circuito: selezioniamo le migliori, con storico di manutenzione completo, e le rimettiamo a disposizione
          con le stesse garanzie di un noleggio a lungo termine tradizionale.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">Risparmio fino al 40%</h3>
        <p>
          Un'auto già immatricolata ha già assorbito la parte più pesante della svalutazione: per questo il canone di un'auto NLT Re-Use
          può costare fino al 40% in meno rispetto alla stessa auto nuova, a parità di servizi inclusi.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">Stock aggiornato ogni ora</h3>
        <p>
          Lo stock che vedi è quello disponibile adesso: si aggiorna ogni ora e le auto assegnate spariscono automaticamente. Per questo
          durano pochissimo: se un'auto ti interessa, compila il modulo o scrivici su WhatsApp e un consulente ti risponde con disponibilità,
          canone e condizioni. Vuoi saperne di più? Leggi <Link to="/noleggio-lungo-termine-usato/" className="font-semibold text-xmove-orange hover:underline" data-testid="usato-link-pillar">come funziona il noleggio lungo termine usato</Link>.
        </p>
      </div>
    </Reveal>
  </section>
);

export default function Usato() {
  const { marca: marcaParam, categoria: categoriaParam } = useParams();
  const landing = categoriaParam ? CATEGORIE_USATO[categoriaParam] : null;
  const [auto, setAuto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [marca, setMarca] = useState("");
  const [alim, setAlim] = useState("");
  const [cambio, setCambio] = useState("");
  const [potenza, setPotenza] = useState("");
  const [tipo, setTipo] = useState("");
  const [ordine, setOrdine] = useState("marca");

  useEffect(() => {
    axios.get(`${API}/usato`).then((r) => setAuto(r.data)).catch(() => setAuto([])).finally(() => setLoading(false));
  }, []);

  // Base della pagina: tutto lo stock, oppure la marca / categoria della pagina di atterraggio
  const marcaLanding = useMemo(() => (marcaParam ? auto.find((a) => slugify(a.marca) === marcaParam)?.marca || null : null), [auto, marcaParam]);
  const base = useMemo(
    () => auto.filter((a) => (!marcaParam || slugify(a.marca) === marcaParam) && (!landing || landing.filtro(a))),
    [auto, marcaParam, landing]
  );
  const path = marcaParam ? `/usato/marca/${marcaParam}/` : categoriaParam ? `/usato/categoria/${categoriaParam}/` : "/usato/";
  const titolo = marcaLanding
    ? `${marcaLanding} usate a noleggio lungo termine (${base.length} a stock) | XMOVE Italia`
    : landing
      ? `${landing.nome} | XMOVE Italia`
      : "Noleggio Lungo Termine Usato: auto SecondLife a stock, fino al 40% in meno | XMOVE Italia";
  const descrizione = marcaLanding
    ? `Noleggio lungo termine usato ${marcaLanding}: ${base.length} auto SecondLife a stock con massimo 70/80.000 km, stesse garanzie del noleggio a lungo termine e risparmio fino al 40%. Stock aggiornato ogni ora.`
    : landing
      ? `${landing.intro} Foto, km certificati, stesse garanzie del noleggio a lungo termine.`
      : "Noleggio lungo termine usato: auto SecondLife (NLT Re-Use) con massimo 70/80.000 km, selezionate una a una, stesse garanzie del noleggio a lungo termine e canone fino al 40% in meno. Stock aggiornato ogni ora.";
  useSeo({
    title: titolo,
    description: descrizione,
    path,
    image: base[0]?.foto,
    noindex: Boolean((marcaParam && !loading && !marcaLanding) || (categoriaParam && !landing)),
    jsonLd: [itemListSchema(base, `${SITE_URL}${path}`), faqSchema(FAQ_USATO)],
  });

  const marche = useMemo(() => [...new Set(auto.map((a) => a.marca))].sort(), [auto]);
  const marcheSelect = useMemo(() => [...new Set(base.map((a) => a.marca))].sort(), [base]);
  const conteggioMarche = useMemo(() => auto.reduce((acc, a) => ({ ...acc, [a.marca]: (acc[a.marca] || 0) + 1 }), {}), [auto]);
  const filtrate = useMemo(() => {
    const t = q.trim().toLowerCase();
    const range = POTENZE.find((p) => p.value === potenza);
    const out = base.filter((a) =>
      (!marca || a.marca === marca) &&
      (!alim || a.alimentazione === alim) &&
      (!cambio || (a.cambio || "").startsWith(cambio)) &&
      (!tipo || (a.tipo_veicolo || "auto") === tipo) &&
      (!range || (cvDi(a) !== null && cvDi(a) >= range.min && cvDi(a) <= range.max)) &&
      (!t || `${a.marca} ${a.modello} ${a.versione}`.toLowerCase().includes(t))
    );
    if (ordine === "km") out.sort((a, b) => (a.km || 0) - (b.km || 0));
    else if (ordine === "anno") out.sort((a, b) => (b.anno || 0) - (a.anno || 0));
    else if (ordine === "prezzo") out.sort((a, b) => (a.prezzo || 9e9) - (b.prezzo || 9e9));
    else if (ordine === "cv") out.sort((a, b) => (cvDi(b) || 0) - (cvDi(a) || 0));
    return out;
  }, [base, q, marca, alim, cambio, potenza, tipo, ordine]);

  const reset = () => { setQ(""); setMarca(""); setAlim(""); setCambio(""); setPotenza(""); setTipo(""); };
  const attivi = [marca, alim, cambio, potenza, tipo, q].filter(Boolean).length;
  const h1 = marcaLanding ? `Noleggio lungo termine usato ${marcaLanding}.` : landing ? landing.h1 : null;

  return (
    <div className="pb-[72px] md:pb-0" data-testid="usato-page">
      <Header />
      <section className="bg-xmove-night pb-14 pt-32 text-white lg:pb-20 lg:pt-40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <nav aria-label="breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs text-white/60" data-testid="usato-breadcrumb">
              <Link to="/" className="hover:text-white">Home</Link><span>/</span>
              {h1 ? <><Link to="/usato/" className="hover:text-white">Usato</Link><span>/</span><span className="text-white">{marcaLanding || landing.nome}</span></> : <span className="text-white">Usato</span>}
            </nav>
            <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-xmove-orange">Noleggio Lungo Termine SecondLife · NLT Re-Use</p>
            {h1 ? (
              <h1 className="mt-3 font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl" data-testid="usato-h1">{h1}</h1>
            ) : (
              <h1 className="mt-3 font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl" data-testid="usato-h1">Noleggio lungo termine usato:<br className="hidden sm:block" /> auto SecondLife selezionate una a una.</h1>
            )}
            <p className="mt-5 max-w-2xl text-base text-white/70 md:text-lg">
              {landing ? landing.intro : <>Vetture che hanno appena concluso un ciclo di noleggio, con massimo 70/80.000 km. Selezioniamo le migliori e le rimettiamo nel circuito del noleggio: le garanzie sono le stesse di un noleggio a lungo termine tradizionale, ma con un <b className="text-white">risparmio fino al 40%</b>. Durano pochissimo sui nostri portali: lo stock si aggiorna ogni ora.</>}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-xmove-cream py-10 lg:py-14">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-3 rounded-2xl border border-xmove-ink/8 bg-white p-4 shadow-soft sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-[1.4fr_repeat(6,minmax(0,1fr))]" data-testid="usato-filters">
            <label className="relative block sm:col-span-2 lg:col-span-4 xl:col-span-1">
              <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-xmove-muted" />
              <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca marca, modello o versione…" data-testid="usato-search" className={`${selectCls} pl-10`} />
            </label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={selectCls} data-testid="usato-filter-tipo">
              <option value="">Tipo veicolo</option>
              {TIPI.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select value={marca} onChange={(e) => setMarca(e.target.value)} className={selectCls} data-testid="usato-filter-marca">
              <option value="">Tutte le marche</option>
              {marcheSelect.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={alim} onChange={(e) => setAlim(e.target.value)} className={selectCls} data-testid="usato-filter-alimentazione">
              <option value="">Alimentazione</option>
              {ALIMENTAZIONI.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={cambio} onChange={(e) => setCambio(e.target.value)} className={selectCls} data-testid="usato-filter-cambio">
              <option value="">Cambio</option>
              <option value="Manuale">Manuale</option>
              <option value="Automatico">Automatico</option>
            </select>
            <select value={potenza} onChange={(e) => setPotenza(e.target.value)} className={selectCls} data-testid="usato-filter-potenza">
              <option value="">Potenza (CV)</option>
              {POTENZE.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <select value={ordine} onChange={(e) => setOrdine(e.target.value)} className={selectCls} data-testid="usato-sort">
              <option value="marca">Ordina: marca</option>
              <option value="km">Km crescenti</option>
              <option value="anno">Più recenti</option>
              <option value="cv">Più potenti</option>
              <option value="prezzo">Prezzo crescente</option>
            </select>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-xmove-muted" data-testid="usato-count">
              <b className="text-xmove-ink">{filtrate.length}</b> veicoli disponibili{attivi ? ` · ${attivi} filtri attivi` : ""}
            </p>
            {attivi > 0 && (
              <button onClick={reset} data-testid="usato-reset" className="flex items-center gap-1.5 text-xs font-bold text-xmove-orange hover:underline"><RotateCcw size={13} /> Azzera filtri</button>
            )}
          </div>

          {loading ? (
            <p className="py-16 text-center text-sm text-xmove-muted">Caricamento stock…</p>
          ) : filtrate.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-20 text-center text-xmove-muted" data-testid="usato-empty">
              <Car size={32} />
              <p className="text-sm">{h1 && base.length === 0 ? "Al momento non ci sono veicoli in questa selezione: guarda tutto lo stock." : "Nessuna auto corrisponde ai filtri. Prova ad allargare la ricerca."}</p>
              {h1 && <Link to="/usato/" className="text-sm font-bold text-xmove-orange hover:underline">Vedi tutto lo stock usato</Link>}
            </div>
          ) : (
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="usato-grid">
              {filtrate.map((a) => <UsatoCard key={a.vehicleid} auto={a} />)}
            </div>
          )}

          {!loading && marche.length > 0 && (
            <nav className="mt-14 rounded-2xl border border-xmove-ink/8 bg-white p-6 shadow-soft" aria-label="Sfoglia lo stock usato" data-testid="usato-link-interni">
              <h2 className="font-display text-lg font-bold text-xmove-ink">Noleggio lungo termine usato per marca</h2>
              <ul className="mt-3 flex flex-wrap gap-2">
                {marche.map((m) => (
                  <li key={m}>
                    <Link to={`/usato/marca/${slugify(m)}/`} className={`inline-block rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${marcaLanding === m ? "border-xmove-orange bg-xmove-orange text-white" : "border-xmove-ink/10 text-xmove-ink hover:border-xmove-orange hover:text-xmove-orange"}`}>
                      {m} <span className="opacity-60">({conteggioMarche[m]})</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <h2 className="mt-6 font-display text-lg font-bold text-xmove-ink">Per tipo</h2>
              <ul className="mt-3 flex flex-wrap gap-2">
                {Object.entries(CATEGORIE_USATO).map(([k, c]) => {
                  const n = auto.filter(c.filtro).length;
                  return n > 0 ? (
                    <li key={k}>
                      <Link to={`/usato/categoria/${k}/`} className={`inline-block rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${categoriaParam === k ? "border-xmove-orange bg-xmove-orange text-white" : "border-xmove-ink/10 text-xmove-ink hover:border-xmove-orange hover:text-xmove-orange"}`}>
                        {c.nome} <span className="opacity-60">({n})</span>
                      </Link>
                    </li>
                  ) : null;
                })}
              </ul>
            </nav>
          )}
        </div>
      </section>

      <TestoUsato landing={landing} />
      <Faq faqs={FAQ_USATO} title="Domande frequenti sul noleggio lungo termine usato" />
      <PreventivoSection idPrefix="usato" title="Interessato a un'auto dello stock?" subtitle="Scrivici quale ti interessa: ti richiamiamo con disponibilità, prezzo e condizioni." />
      <Footer />
      <MobileStickyBar />
    </div>
  );
}
